import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiRequest } from "../../service/apiRequest";
import { MasterState, initialState } from "../../types/MasterState";
import {
  Categories,
  Employee,
  Wood,
  WoodType,
  Ledger,
  Products,
  UnitType,
} from "../../types/CreateProductType";
import { Caring } from "../../types/Caring";

//
// 🔹 1. BULK FETCH (Initial Load)
//
export const fetchMasterData = createAsyncThunk(
  "master/fetchMasterData",
  async () => {
    const results = await Promise.allSettled([
      apiRequest<Categories[]>({ method: "get", url: "category?status=1", silent: true }),
      apiRequest<Employee[]>({ method: "get", url: "employees?status=1", silent: true }),
      apiRequest<Wood[]>({ method: "get", url: "woods?status=1", silent: true }),
      apiRequest<WoodType[]>({ method: "get", url: "wood-types?status=1", silent: true }),
      apiRequest<{ ledgers: Ledger[] }>({
        method: "get",
        url: "h-ledger-data?status=1",
        silent: true,
      }),
      apiRequest<{ data: Products[] }>({
        method: "get",
        url: "get-all-handicraft-products?status=1",
        silent: true,
      }),
      apiRequest<UnitType[]>({ method: "get", url: "unit-types?status=1", silent: true }),
    ]);

    const categoriesData = results[0].status === "fulfilled" ? results[0].value : [];
    const employeeData = results[1].status === "fulfilled" ? results[1].value : [];
    const woodData = results[2].status === "fulfilled" ? results[2].value : [];
    const woodtypeData = results[3].status === "fulfilled" ? results[3].value : [];
    const ledgerData = results[4].status === "fulfilled" ? results[4].value : { ledgers: [] };
    const productData = results[5].status === "fulfilled" ? results[5].value : { data: [] };
    const unittypeData = results[6].status === "fulfilled" ? results[6].value : [];

    console.log("Master data fetched, wood count:", Array.isArray(woodData) ? woodData.length : 0);
    return {
      categories: Array.isArray(categoriesData) ? categoriesData : [],
      employees: Array.isArray(employeeData) ? employeeData : [],
      woods: Array.isArray(woodData) ? woodData : [],
      woodtypes: Array.isArray(woodtypeData) ? woodtypeData : [],
      ledgers: Array.isArray(ledgerData?.ledgers) ? ledgerData.ledgers : [],
      products: Array.isArray((productData as any)?.data)
        ? (productData as any).data
        : Array.isArray(productData)
          ? productData
          : [],
      unittypes: Array.isArray((unittypeData as any)?.data)
        ? (unittypeData as any).data
        : Array.isArray(unittypeData)
          ? unittypeData
          : [],
    };
  },
);

//
// 🔹 2. INDIVIDUAL DATASET THUNKS (For selective reload)
//
export const fetchCategory = createAsyncThunk(
  "master/fetchCategory",
  async () =>
    await apiRequest<Categories[]>({ method: "get", url: "category?status=1", silent: true }),
);
export const fetchEmployeeData = createAsyncThunk(
  "master/fetchEmployeeData",
  async () =>
    await apiRequest<Employee[]>({ method: "get", url: "employees?status=1", silent: true }),
);
export const fetchWoodData = createAsyncThunk(
  "master/fetchWoodData",
  async () => {
    try {
      const res = await apiRequest<Wood[]>({ method: "get", url: "woods?status=1", silent: true });
      if (Array.isArray(res) && res.length > 0) return res;
      const res2 = await apiRequest<Wood[]>({ method: "get", url: "woods", silent: true });
      return Array.isArray(res2) ? res2 : [];
    } catch {
      try {
        const res2 = await apiRequest<Wood[]>({ method: "get", url: "woods", silent: true });
        return Array.isArray(res2) ? res2 : [];
      } catch {
        return [];
      }
    }
  },
);
export const fetchWoodTypeData = createAsyncThunk(
  "master/fetchWoodTypeData",
  async () => {
    try {
      const res = await apiRequest<WoodType[]>({ method: "get", url: "wood-types?status=1", silent: true });
      if (Array.isArray(res) && res.length > 0) return res;
      const res2 = await apiRequest<WoodType[]>({ method: "get", url: "wood-types", silent: true });
      return Array.isArray(res2) ? res2 : [];
    } catch {
      try {
        const res2 = await apiRequest<WoodType[]>({ method: "get", url: "wood-types", silent: true });
        return Array.isArray(res2) ? res2 : [];
      } catch {
        return [];
      }
    }
  },
);
export const fetcLedgerData = createAsyncThunk(
  "master/fetcLedgerData",
  async () =>
    await apiRequest<{ ledgers: Ledger[] }>({
      method: "get",
      url: "h-ledger-data?status=1",
      silent: true,
    }),
);
export const fetcProductData = createAsyncThunk(
  "master/fetcProductData",
  async () => {
    try {
      const res = await apiRequest<any>({
        method: "get",
        url: "get-all-handicraft-products?status=1",
        silent: true,
      });
      return res;
    } catch {
      try {
        const res = await apiRequest<any>({
          method: "get",
          url: "get-all-handicraft-products",
          silent: true,
        });
        return res;
      } catch {
        return { data: [] };
      }
    }
  },
);
export const fetcunittypeData = createAsyncThunk(
  "master/fetcunittypeData",
  async () => {
    try {
      const res = await apiRequest<any>({
        method: "get",
        url: "unit-types?status=1",
        silent: true,
      });
      return res;
    } catch {
      try {
        const res = await apiRequest<any>({
          method: "get",
          url: "unit-types",
          silent: true,
        });
        return res;
      } catch {
        return { data: [] };
      }
    }
  },
);
export const fetchUsedBomProductIds = createAsyncThunk(
  "master/fetchUsedBomProductIds",
  async () =>
    await apiRequest<number[]>({
      method: "get",
      url: "get-used-bom-product-ids",
      silent: true,
    }),
);

//
// 🔹 3. SLICE
//
const masterSlice = createSlice({
  name: "master",
  initialState,
  reducers: {
    resetMaster: () => initialState, // useful on logout
  },
  extraReducers: (builder) => {
    builder

      // ===== BULK LOAD =====
      .addCase(fetchMasterData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchMasterData.fulfilled,
        (
          state,
          action: PayloadAction<
            Omit<MasterState, "loading" | "loaded" | "usedBomProductIds">
          >, // ← usedBomProductIds भी Omit करें
        ) => {
          Object.assign(state, action.payload);
          state.loading = false;
          state.loaded = true;
        },
      )
      .addCase(fetchMasterData.rejected, (state) => {
        state.loading = false;
      })
      // ===== INDIVIDUAL RELOADS =====
      .addCase(fetchCategory.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchEmployeeData.fulfilled, (state, action) => {
        state.employees = action.payload;
      })
      .addCase(fetchWoodData.fulfilled, (state, action) => {
        state.woods = action.payload;
      })
      .addCase(fetchWoodTypeData.fulfilled, (state, action) => {
        state.woodtypes = action.payload;
      })
      .addCase(fetcLedgerData.fulfilled, (state, action) => {
        state.ledgers = action.payload.ledgers;
      })
      .addCase(fetcProductData.fulfilled, (state, action) => {
        state.products = (action.payload as any)?.data || (Array.isArray(action.payload) ? action.payload : []);
      })
      .addCase(fetcunittypeData.fulfilled, (state, action) => {
        state.unittypes = (action.payload as any)?.data || (Array.isArray(action.payload) ? action.payload : []);
      })
      .addCase(fetchUsedBomProductIds.fulfilled, (state, action) => {
        state.usedBomProductIds = action.payload;
      });
  },
});

export const { resetMaster } = masterSlice.actions;
export default masterSlice.reducer;
